import type { SuspenseQueryHookOptions } from "@apollo/client";
import { skipToken } from "@apollo/client";
import { useMutationHandler } from "@sinch/hooks/useMutationHandler";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import {
  ArchiveInterval,
  CreateInterval,
  DeleteInterval,
  EditInterval,
  RateInterval,
  RateIntervalList,
} from "./queries.graphql";
import type {
  ArchiveIntervalMutation,
  ArchiveIntervalMutationVariables,
  CreateIntervalMutation,
  CreateIntervalMutationVariables,
  DeleteIntervalMutation,
  DeleteIntervalMutationVariables,
  EditIntervalMutation,
  EditIntervalMutationVariables,
  RateIntervalListQuery,
  RateIntervalListQueryVariables,
  RateIntervalQuery,
  RateIntervalQueryVariables,
} from "./queries.types";
import { isString } from "ramda-adjunct";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";

interface UseRateIntervalProps<TId extends string | undefined | null = string | undefined | null> {
  id?: TId;
  queryOptions?: Omit<SuspenseQueryHookOptions<RateIntervalQuery, RateIntervalQueryVariables>, "variables">;
}

interface UseRateIntervalReturn<TId extends string | undefined | null = string | undefined | null> {
  archive: ReturnType<typeof useMutationHandler<ArchiveIntervalMutation, ArchiveIntervalMutationVariables>>[0];
  create: ReturnType<typeof useMutationHandler<CreateIntervalMutation, CreateIntervalMutationVariables>>[0];
  edit: ReturnType<typeof useMutationHandler<EditIntervalMutation, EditIntervalMutationVariables>>[0];
  remove: ReturnType<typeof useMutationHandler<DeleteIntervalMutation, DeleteIntervalMutationVariables>>[0];
  loading: boolean;
  rateInterval: (TId extends string ? RateIntervalQuery : RateIntervalListQuery) | undefined;
  getLabel: (intervalId: TId extends string ? never : string | null | undefined) => string | null;
}

export const useRateInterval = <TId extends string | undefined | null = string | undefined | null>(
  props?: UseRateIntervalProps<TId>
): UseRateIntervalReturn<TId> => {
  const id = props?.id;

  const [archiveInterval, { loading: archiveLoading }] = useMutationHandler<
    ArchiveIntervalMutation,
    ArchiveIntervalMutationVariables
  >(ArchiveInterval);
  const [deleteInterval, { loading: deleteLoading }] = useMutationHandler<
    DeleteIntervalMutation,
    DeleteIntervalMutationVariables
  >(DeleteInterval);
  const [createInterval, { loading: createLoading }] = useMutationHandler<
    CreateIntervalMutation,
    CreateIntervalMutationVariables
  >(CreateInterval);
  const [editInterval, { loading: editLoading }] = useMutationHandler<
    EditIntervalMutation,
    EditIntervalMutationVariables
  >(EditInterval);

  const { data } = useSuspenseQueryHandler<RateIntervalQuery, RateIntervalQueryVariables>(
    RateInterval,
    id ? { id } : undefined,
    id ? (props?.queryOptions ?? {}) : skipToken
  );

  const { data: list } = useQueryHandler<RateIntervalListQuery, RateIntervalListQueryVariables>(
    RateIntervalList,
    undefined,
    {
      skip: !!id,
      ...(props?.queryOptions ?? {}),
    }
  );

  const getLabel = (intervalId: TId extends string ? never : string | null | undefined) => {
    if (!intervalId) {
      return null;
    }
    if (props?.id) {
      return data?.rateInterval?.name ?? null;
    }
    return list?.rateIntervals?.find(({ id }) => id === intervalId)?.name ?? null;
  };

  return {
    archive: archiveInterval,
    create: createInterval,
    edit: editInterval,
    remove: deleteInterval,
    loading: deleteLoading || editLoading || createLoading || archiveLoading,
    rateInterval: (isString(props?.id) ? data : list) as UseRateIntervalReturn<TId>["rateInterval"],
    getLabel,
  };
};
